/* Shared cursor-height hover caption (the signature Onni image interaction).
   The caption rides at the cursor's vertical position over the image with a
   soft lerp. Used by featured products and the explore (CTA) blocks.

   Markup contract:
     [data-cursor-card]  — the card root (gets .is-hover while hovered)
       [data-cursor-media] — the image area the caption travels over
         [data-cursor-cap] — the caption (absolute; JS sets translateY)

   Desktop fine-pointer only. On touch, sections provide their own fallback
   (featured: marquee; explore: a sticky caption that rides with the scroll).
   Registry-managed: re-inits after every Barba swap; listeners + rAF loops
   carry the page's abort signal, so re-running never double-binds. */
(() => {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PAD = 24; // keep the caption comfortably inside the image edges
  const LERP = 0.14;

  const init = (ctx) => {
    const signal = ctx && ctx.signal;

    document.querySelectorAll('[data-cursor-card]').forEach((card) => {
      const media = card.querySelector('[data-cursor-media]');
      const cap = card.querySelector('[data-cursor-cap]');
      if (!media || !cap) return;

      let target = 0;
      let current = 0;
      let active = false;
      let raf = null;
      let lastClientY = null;

      const clampY = (y) => {
        const max = media.offsetHeight - cap.offsetHeight - PAD;
        return Math.max(PAD, Math.min(y, max));
      };

      const cursorY = (clientY) =>
        clampY(clientY - media.getBoundingClientRect().top - cap.offsetHeight / 2);

      const apply = () => {
        cap.style.transform = `translateY(${current.toFixed(2)}px)`;
      };

      const tick = () => {
        current += (target - current) * LERP;
        apply();
        if (active || Math.abs(target - current) > 0.5) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
        }
      };

      if (signal) {
        signal.addEventListener('abort', () => {
          if (raf) cancelAnimationFrame(raf);
          raf = null;
          active = false;
        });
      }

      media.addEventListener('pointerenter', (event) => {
        if (!finePointer.matches || event.pointerType === 'touch') return;
        active = true;
        lastClientY = event.clientY;
        // start at the cursor: no fly-in from the previous position
        target = current = cursorY(event.clientY);
        apply();
        card.classList.add('is-hover');
        if (!raf && !reducedMotion.matches) raf = requestAnimationFrame(tick);
      }, { signal });

      media.addEventListener('pointermove', (event) => {
        if (!active) return;
        lastClientY = event.clientY;
        target = cursorY(event.clientY);
        if (reducedMotion.matches) {
          current = target;
          apply();
        }
      }, { signal });

      // keep tracking while the page scrolls under a resting cursor
      window.addEventListener(
        'scroll',
        () => {
          if (active && lastClientY !== null) target = cursorY(lastClientY);
        },
        { passive: true, signal }
      );

      media.addEventListener('pointerleave', () => {
        active = false;
        card.classList.remove('is-hover');
      }, { signal });
    });
  };

  if (window.Onni && window.Onni.register) {
    window.Onni.register('cursor-cap', init);
  } else {
    init();
  }
})();
